import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLocale } from '../hooks/useLocale.jsx';
import { isNativeApp } from '../services/platform-service.js';
import { analytics, logEvent } from '../firebase.js';
import { currentAnnouncement, markAnnouncementSeen, pickText } from '../services/announcement-service.js';
import { isOnboardingDone } from './Onboarding.jsx';
import { isTabBarHidden } from './MobileTabBar.jsx';

// 서버 공지 (config/announcement). 둘러보기 투어·환영 화면·로그인 창이 먼저다 —
// blocked 동안과 투어를 아직 안 본 동안은 띄우지 않는다. 화면을 옮길 때와 앱이
// 다시 앞으로 올 때 다시 확인한다 (서비스가 5분 캐시).
export function AnnouncementModal({ user, blocked = false }) {
  const { t, lang } = useLocale();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [ann, setAnn] = useState(null);
  const [tick, setTick] = useState(0);
  const signedIn = !!user && !user.isAnonymous;
  const ready = !blocked && isOnboardingDone() && !isTabBarHidden(pathname) && pathname !== '/admin';

  useEffect(() => {
    if (!isNativeApp()) return undefined;
    let listener;
    let cancelled = false;
    import('@capacitor/app').then(async ({ App }) => {
      if (cancelled) return;
      listener = await App.addListener('resume', () => setTick((n) => n + 1));
    });
    return () => { cancelled = true; listener?.remove?.(); };
  }, []);

  useEffect(() => {
    if (!ready || ann) return undefined;
    let alive = true;
    currentAnnouncement({ signedIn }).then((a) => {
      if (!alive || !a) return;
      setAnn(a);
      logEvent(analytics, 'announcement_view', { id: a.id });
    });
    return () => { alive = false; };
  }, [ready, signedIn, pathname, tick, ann]);

  if (!ann || !ready) return null;

  const dismiss = () => { markAnnouncementSeen(ann.id); setAnn(null); };
  const close = () => { logEvent(analytics, 'announcement_close', { id: ann.id }); dismiss(); };
  const to = typeof ann.cta?.to === 'string' ? ann.cta.to : '';
  const ctaLabel = pickText(ann.cta?.label, lang);
  const go = () => {
    logEvent(analytics, 'announcement_click', { id: ann.id });
    dismiss();
    if (to.startsWith('/')) navigate(to);
    else if (/^https:\/\//.test(to)) window.open(to, '_blank', 'noopener');
  };

  return (
    <div className="modal-backdrop" onClick={close}>
      <div className="modal announcement-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={close} aria-label={t('close')}>
          <i className="material-symbols-rounded">close</i>
        </button>
        <h3 className="announcement-title">{pickText(ann.title, lang)}</h3>
        {ann.body && <p className="announcement-body">{pickText(ann.body, lang)}</p>}
        <div className="announcement-actions">
          {to && ctaLabel
            ? <button type="button" className="btn btn-primary btn-block" onClick={go}>{ctaLabel}</button>
            : <button type="button" className="btn btn-primary btn-block" onClick={close}>{t('close')}</button>}
        </div>
      </div>
    </div>
  );
}
